import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon, ChevronRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { MonthDetailResponse } from '@/services/bankRecon'
import { ColumnResizeHandle, SearchInput, useColumnWidths } from '@/tableControls'
import { toast } from '@/store/toastStore'
import { formatCalendarDate } from '@/utils/dateFormat'
import { filterRowsByText } from '../fixed-assets/fixedAssetsTableFilters'
import { formatBankReconTableAmount } from './bankReconFormat'
import {
  canLinkSelection,
  collectLinkerQboTxns,
  collectLinkerStatementLines,
  computeLinkerDifference,
  groupLinkedMatches,
  isLinkerSelectionReady,
  linkedMatchGroupReactKey,
  sumSignedAmounts,
  uniqueLineAmountsForLinkedGroup,
  uniqueTxnAmountsForLinkedGroup,
  type LinkedMatchGroup,
  type LinkerQboRow,
  type LinkerStatementLine,
} from './bankReconLinkerHelpers'
import { fixedColumnStyle } from './bankReconTableConfig'

const STATEMENT_PAGE_KEY = 'bank-recon-linker-statement'
const QBO_PAGE_KEY = 'bank-recon-linker-qbo'

const STATEMENT_COLUMNS = ['date', 'description', 'amount'] as const
const QBO_COLUMNS = ['date', 'type', 'doc_number', 'name_memo', 'amount', 'cleared'] as const
const SELECTED_ROW_CLASS =
  'cursor-pointer select-none bg-blue-50 [&>td]:bg-blue-50 hover:[&>td]:bg-blue-50'
const UNSELECTED_ROW_CLASS = 'cursor-pointer select-none hover:bg-gray-50 hover:[&>td]:bg-gray-50'
const IGNORED_ROW_CLASS =
  'select-none bg-gray-100 text-gray-500 [&>td]:bg-gray-100 [&>td]:text-gray-500'
const LINKED_ROW_CLASS =
  'select-none bg-emerald-50 text-emerald-900 [&>td]:bg-emerald-50 [&>td]:text-emerald-900'
const LINK_TOTALS_MISMATCH_MESSAGE = 'Statement and QBO totals must match before linking.'
const METRIC_VALUE_ROW_CLASS =
  'mt-0.5 flex h-6 items-center font-semibold tabular-nums text-gray-900'
const DEFAULT_LINK_NOTE = 'Manual link'

type LinkedRowsVisibility = 'show' | 'hide'
type AllUnreconciledVisibility = 'show' | 'hide'

function SegmentedVisibilityToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
  ariaLabel: string
}) {
  return (
    <div
      className="inline-flex rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
            value === option.value
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

function LinkedRowsVisibilityToggle({
  value,
  onChange,
}: {
  value: LinkedRowsVisibility
  onChange: (value: LinkedRowsVisibility) => void
}) {
  return (
    <SegmentedVisibilityToggle
      value={value}
      onChange={onChange}
      ariaLabel="Matched rows visibility"
      options={[
        { value: 'hide', label: 'Hide' },
        { value: 'show', label: 'Show' },
      ]}
    />
  )
}

function AllUnreconciledVisibilityToggle({
  value,
  onChange,
}: {
  value: AllUnreconciledVisibility
  onChange: (value: AllUnreconciledVisibility) => void
}) {
  return (
    <SegmentedVisibilityToggle
      value={value}
      onChange={onChange}
      ariaLabel="All unreconciled visibility"
      options={[
        { value: 'hide', label: 'Hide' },
        { value: 'show', label: 'Show' },
      ]}
    />
  )
}

const STATEMENT_WIDTHS: Record<string, number> = {
  date: 110,
  description: 240,
  amount: 110,
}

const QBO_WIDTHS: Record<string, number> = {
  date: 110,
  type: 160,
  doc_number: 130,
  name_memo: 200,
  amount: 110,
  cleared: 100,
}

const STATEMENT_LABELS: Record<(typeof STATEMENT_COLUMNS)[number], string> = {
  date: 'Date',
  description: 'Description',
  amount: 'Amount',
}

const QBO_LABELS: Record<(typeof QBO_COLUMNS)[number], string> = {
  date: 'Date',
  type: 'Type',
  doc_number: 'Doc Ref #',
  name_memo: 'Name / memo',
  amount: 'Amount',
  cleared: 'Cleared',
}

type QboContextMenuState = {
  txnId: string
  ignored: boolean
  linked: boolean
  ignoreOverrideId?: number
  linkOverrideIds?: number[]
  matchLineKey?: string
  x: number
  y: number
}

function clampContextMenuPosition(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } {
  const padding = 8
  const maxX = Math.max(padding, window.innerWidth - width - padding)
  const maxY = Math.max(padding, window.innerHeight - height - padding)
  return {
    x: Math.min(Math.max(x, padding), maxX),
    y: Math.min(Math.max(y, padding), maxY),
  }
}

function QboRowContextMenu({
  ignored,
  linked,
  busy,
  position,
  onClose,
  onIgnore,
  onRemoveIgnore,
  onUnlink,
}: {
  ignored: boolean
  linked: boolean
  busy: boolean
  position: { x: number; y: number }
  onClose: () => void
  onIgnore: () => void
  onRemoveIgnore: () => void
  onUnlink: () => void
}) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [clampedPosition, setClampedPosition] = useState(position)

  useLayoutEffect(() => {
    const menu = menuRef.current
    if (!menu) {
      setClampedPosition(position)
      return
    }
    const rect = menu.getBoundingClientRect()
    setClampedPosition(clampContextMenuPosition(position.x, position.y, rect.width, rect.height))
  }, [position.x, position.y])

  useEffect(() => {
    function handlePointerDown(event: Event) {
      const target = event.target
      if (target instanceof Node && menuRef.current?.contains(target)) return
      onClose()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    function handleScroll() {
      onClose()
    }

    window.addEventListener('mousedown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      window.removeEventListener('mousedown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [onClose])

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[10rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
      style={{ left: clampedPosition.x, top: clampedPosition.y }}
      role="menu"
    >
      {linked ? (
        <button
          type="button"
          role="menuitem"
          className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            onUnlink()
            onClose()
          }}
        >
          Unlink
        </button>
      ) : ignored ? (
        <button
          type="button"
          role="menuitem"
          className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            onRemoveIgnore()
            onClose()
          }}
        >
          Remove ignore
        </button>
      ) : (
        <button
          type="button"
          role="menuitem"
          className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            onIgnore()
            onClose()
          }}
        >
          Ignore QBO transaction
        </button>
      )}
    </div>,
    document.body,
  )
}

type Props = {
  detail: MonthDetailResponse
  canEdit: boolean
  onLink: (payload: {
    statementLineKeys: string[]
    qboTxnIds: string[]
    note: string
  }) => Promise<void>
  onIgnore: (payload: {
    kind: 'ignore_qbo_txn'
    qboTxnId: string
    note: string
  }) => Promise<void>
  onRemoveOverride: (overrideId: number) => Promise<void>
  onUnlinkOverrides: (overrideIds: number[]) => Promise<void>
}

function statementSearchText(line: LinkerStatementLine): string {
  return [line.posted_date, line.description, line.amount_signed].join(' ')
}

function qboSearchText(txn: LinkerQboRow): string {
  return [
    txn.txn_date,
    txn.txn_type,
    txn.doc_number,
    txn.name,
    txn.memo,
    txn.amount_signed,
    txn.cleared_status,
  ]
    .filter(Boolean)
    .join(' ')
}

function linkedGroupSearchText(group: LinkedMatchGroup): string {
  const parts: string[] = []
  for (const lineEntry of group.lines) {
    parts.push(
      lineEntry.line?.posted_date ?? '',
      lineEntry.line?.description ?? '',
      lineEntry.line?.amount_signed ?? '',
      lineEntry.lineKey,
    )
  }
  for (const item of group.items) {
    const txn = item.txn
    if (txn) {
      parts.push(
        txn.txn_date,
        txn.txn_type,
        txn.doc_number ?? '',
        txn.name ?? '',
        txn.memo ?? '',
        txn.amount_signed ?? '',
      )
    }
  }
  return parts.filter(Boolean).join(' ')
}

export default function BankReconLinker({
  detail,
  canEdit,
  onLink,
  onIgnore,
  onRemoveOverride,
  onUnlinkOverrides,
}: Props) {
  const [manualMatchSearch, setManualMatchSearch] = useState('')
  const [linkedRowsVisibility, setLinkedRowsVisibility] = useState<LinkedRowsVisibility>('hide')
  const [allUnreconciledVisibility, setAllUnreconciledVisibility] =
    useState<AllUnreconciledVisibility>('hide')
  const [collapsed, setCollapsed] = useState(false)
  const [selectedStatementKeys, setSelectedStatementKeys] = useState<Set<string>>(() => new Set())
  const [selectedQboTxnIds, setSelectedQboTxnIds] = useState<Set<string>>(() => new Set())
  const [ignoring, setIgnoring] = useState(false)
  const [qboContextMenu, setQboContextMenu] = useState<QboContextMenuState | null>(null)
  const [linkedThisMonthExpanded, setLinkedThisMonthExpanded] = useState(false)
  const [linkedThisMonthSearch, setLinkedThisMonthSearch] = useState('')
  const lastStatementIndexRef = useRef<number | null>(null)
  const lastQboIndexRef = useRef<number | null>(null)

  const statementLines = useMemo(() => collectLinkerStatementLines(detail), [detail])
  const qboTxns = useMemo(
    () =>
      collectLinkerQboTxns(detail, {
        includeAllUnreconciled: allUnreconciledVisibility === 'show',
      }),
    [detail, allUnreconciledVisibility],
  )
  const linkedGroups = useMemo(() => groupLinkedMatches(detail), [detail])
  const filteredLinkedGroups = useMemo(
    () => filterRowsByText(linkedGroups, linkedThisMonthSearch, linkedGroupSearchText),
    [linkedGroups, linkedThisMonthSearch],
  )

  const filteredStatementLines = useMemo(
    () => filterRowsByText(statementLines, manualMatchSearch, statementSearchText),
    [statementLines, manualMatchSearch],
  )
  const filteredQboTxns = useMemo(
    () => filterRowsByText(qboTxns, manualMatchSearch, qboSearchText),
    [qboTxns, manualMatchSearch],
  )
  const showLinkedRows = linkedRowsVisibility === 'show'
  const visibleStatementLines = useMemo(
    () =>
      showLinkedRows
        ? filteredStatementLines
        : filteredStatementLines.filter((line) => !line.linked),
    [filteredStatementLines, showLinkedRows],
  )
  const visibleQboTxns = useMemo(
    () =>
      showLinkedRows ? filteredQboTxns : filteredQboTxns.filter((txn) => !txn.linked),
    [filteredQboTxns, showLinkedRows],
  )

  const selectedStatementLines = useMemo(
    () => statementLines.filter((line) => selectedStatementKeys.has(line.line_key)),
    [statementLines, selectedStatementKeys],
  )
  const selectedQboTxns = useMemo(
    () => qboTxns.filter((txn) => selectedQboTxnIds.has(txn.qbo_line_key || txn.txn_id)),
    [qboTxns, selectedQboTxnIds],
  )

  const statementSum = sumSignedAmounts(selectedStatementLines.map((line) => line.amount_signed))
  const qboSum = sumSignedAmounts(selectedQboTxns.map((txn) => txn.amount_signed))
  const difference = computeLinkerDifference(
    selectedStatementLines.map((line) => line.amount_signed),
    selectedQboTxns.map((txn) => txn.amount_signed),
  )
  const linkEnabled = canLinkSelection(
    canEdit,
    selectedStatementKeys,
    selectedQboTxnIds,
    difference,
  )
  const selectionReady = isLinkerSelectionReady(selectedStatementKeys, selectedQboTxnIds)
  const showDifferenceAlert = difference !== 0
  const totalsBalanced = selectionReady && !showDifferenceAlert

  const { getWidth: getStatementWidth, startResize: startStatementResize } = useColumnWidths({
    pageKey: STATEMENT_PAGE_KEY,
    columnKeys: [...STATEMENT_COLUMNS],
    defaults: STATEMENT_WIDTHS,
  })
  const { getWidth: getQboWidth, startResize: startQboResize } = useColumnWidths({
    pageKey: QBO_PAGE_KEY,
    columnKeys: [...QBO_COLUMNS],
    defaults: QBO_WIDTHS,
  })

  const clearSelection = useCallback(() => {
    setSelectedStatementKeys(new Set())
    setSelectedQboTxnIds(new Set())
  }, [])

  const toggleStatementRow = useCallback((lineKey: string) => {
    setSelectedStatementKeys((prev) => {
      const next = new Set(prev)
      if (next.has(lineKey)) next.delete(lineKey)
      else next.add(lineKey)
      return next
    })
  }, [])

  const toggleQboRow = useCallback((qboLineKey: string) => {
    setSelectedQboTxnIds((prev) => {
      const next = new Set(prev)
      if (next.has(qboLineKey)) next.delete(qboLineKey)
      else next.add(qboLineKey)
      return next
    })
  }, [])

  const handleStatementRowClick = useCallback(
    (line: LinkerStatementLine, index: number, event: ReactMouseEvent<HTMLTableRowElement>) => {
      if (!canEdit || line.linked) return

      if (event.shiftKey && lastStatementIndexRef.current !== null) {
        event.preventDefault()
        const start = Math.min(lastStatementIndexRef.current, index)
        const end = Math.max(lastStatementIndexRef.current, index)
        setSelectedStatementKeys((prev) => {
          const next = new Set(prev)
          for (let rowIndex = start; rowIndex <= end; rowIndex += 1) {
            const row = visibleStatementLines[rowIndex]
            if (row && !row.linked) next.add(row.line_key)
          }
          return next
        })
        return
      }

      lastStatementIndexRef.current = index
      toggleStatementRow(line.line_key)
    },
    [canEdit, toggleStatementRow, visibleStatementLines],
  )

  const handleQboRowClick = useCallback(
    (txn: LinkerQboRow, index: number, event: ReactMouseEvent<HTMLTableRowElement>) => {
      if (!canEdit || txn.ignored || txn.linked) return

      if (event.shiftKey && lastQboIndexRef.current !== null) {
        event.preventDefault()
        const start = Math.min(lastQboIndexRef.current, index)
        const end = Math.max(lastQboIndexRef.current, index)
        setSelectedQboTxnIds((prev) => {
          const next = new Set(prev)
          for (let rowIndex = start; rowIndex <= end; rowIndex += 1) {
            const row = visibleQboTxns[rowIndex]
            if (row && !row.ignored && !row.linked) {
              next.add(row.qbo_line_key || row.txn_id)
            }
          }
          return next
        })
        return
      }

      lastQboIndexRef.current = index
      toggleQboRow(txn.qbo_line_key || txn.txn_id)
    },
    [canEdit, toggleQboRow, visibleQboTxns],
  )

  const handleLink = useCallback(async () => {
    if (!canEdit || !selectionReady) return
    if (difference !== 0) {
      toast.error(LINK_TOTALS_MISMATCH_MESSAGE)
      return
    }
    const statementLineKeys = [...selectedStatementKeys]
    const qboLineKeys = [...selectedQboTxnIds]
    clearSelection()
    try {
      await onLink({
        statementLineKeys,
        qboTxnIds: qboLineKeys,
        note: DEFAULT_LINK_NOTE,
      })
    } catch {
      // Parent rolls back optimistic detail and surfaces toast.
    }
  }, [
    canEdit,
    clearSelection,
    difference,
    onLink,
    selectedQboTxnIds,
    selectedStatementKeys,
    selectionReady,
  ])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'l' && event.key !== 'L') return
      const target = event.target
      if (
        target instanceof HTMLElement
        && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      if (!selectionReady) return
      event.preventDefault()
      void handleLink()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleLink, selectionReady])

  const handleIgnoreQbo = async (txnId: string) => {
    if (!canEdit) return
    setIgnoring(true)
    try {
      await onIgnore({
        kind: 'ignore_qbo_txn',
        qboTxnId: txnId,
        note: DEFAULT_LINK_NOTE,
      })
      setSelectedQboTxnIds((prev) => {
        if (!prev.has(txnId)) return prev
        const next = new Set(prev)
        next.delete(txnId)
        return next
      })
    } finally {
      setIgnoring(false)
    }
  }

  const handleRemoveQboIgnore = async (overrideId: number, txnId: string) => {
    if (!canEdit) return
    setIgnoring(true)
    try {
      await onRemoveOverride(overrideId)
      setSelectedQboTxnIds((prev) => {
        if (!prev.has(txnId)) return prev
        const next = new Set(prev)
        next.delete(txnId)
        return next
      })
    } finally {
      setIgnoring(false)
    }
  }

  const handleUnlinkGroup = async (overrideIds: number[], lineKeys: string[]) => {
    if (!canEdit || overrideIds.length === 0) return
    setSelectedQboTxnIds((prev) => {
      const linkedLineKeys = new Set(
        qboTxns
          .filter((txn) => txn.linked && txn.linkOverrideIds?.some((id) => overrideIds.includes(id)))
          .map((txn) => txn.qbo_line_key || txn.txn_id),
      )
      if (![...prev].some((lineKey) => linkedLineKeys.has(lineKey))) return prev
      const next = new Set(prev)
      for (const lineKey of linkedLineKeys) next.delete(lineKey)
      return next
    })
    setSelectedStatementKeys((prev) => {
      if (![...prev].some((lineKey) => lineKeys.includes(lineKey))) return prev
      const next = new Set(prev)
      for (const lineKey of lineKeys) next.delete(lineKey)
      return next
    })
    try {
      await onUnlinkOverrides(overrideIds)
    } catch {
      // Parent rolls back optimistic detail and surfaces toast.
    }
  }

  const renderStatementCell = (
    columnKey: (typeof STATEMENT_COLUMNS)[number],
    line: LinkerStatementLine,
    style: CSSProperties,
  ) => {
    switch (columnKey) {
      case 'date':
        return (
          <td key={columnKey} className="tabular-nums" style={style}>
            {formatCalendarDate(line.posted_date)}
          </td>
        )
      case 'description':
        return (
          <td key={columnKey} style={style}>
            {line.description}
          </td>
        )
      case 'amount':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatBankReconTableAmount(line.amount_signed)}
          </td>
        )
      default:
        return <td key={columnKey} style={style}>—</td>
    }
  }

  const renderQboCell = (
    columnKey: (typeof QBO_COLUMNS)[number],
    txn: LinkerQboRow,
    style: CSSProperties,
  ) => {
    switch (columnKey) {
      case 'date':
        return (
          <td key={columnKey} className="tabular-nums" style={style}>
            {formatCalendarDate(txn.txn_date)}
          </td>
        )
      case 'type': {
        const typeLabel = txn.txn_type || '—'
        const linkClass = txn.ignored
          ? 'text-gray-500 hover:text-gray-600 hover:underline'
          : txn.linked
            ? 'text-emerald-700 hover:text-emerald-900 hover:underline'
            : 'text-blue-600 hover:text-blue-800 hover:underline'
        return (
          <td key={columnKey} style={style}>
            {txn.url && typeLabel !== '—' ? (
              <a
                href={txn.url}
                target="_blank"
                rel="noreferrer"
                className={linkClass}
                onClick={(event) => {
                  if (!event.ctrlKey && !event.metaKey) {
                    event.preventDefault()
                  }
                }}
              >
                {typeLabel}
              </a>
            ) : (
              typeLabel
            )}
          </td>
        )
      }
      case 'doc_number':
        return (
          <td key={columnKey} style={style}>
            {txn.doc_number || '—'}
          </td>
        )
      case 'name_memo':
        return (
          <td key={columnKey} style={style}>
            {[txn.name, txn.memo].filter(Boolean).join(' — ') || '—'}
          </td>
        )
      case 'amount':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatBankReconTableAmount(txn.amount_signed)}
          </td>
        )
      case 'cleared':
        return (
          <td key={columnKey} style={style}>
            {txn.cleared_status ?? '—'}
          </td>
        )
      default:
        return <td key={columnKey} style={style}>—</td>
    }
  }

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setCollapsed((prev) => !prev)
          }
        }}
        className="flex cursor-pointer select-none items-start gap-2.5 border-b border-gray-200 px-5 py-4 hover:bg-gray-50"
        aria-expanded={!collapsed}
      >
        <ChevronDownIcon
          className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
          aria-hidden="true"
        />
        <div>
          <h2 className="text-base font-semibold text-gray-900">Manual matches</h2>
          <p className="mt-1 text-xs text-gray-500">
            Select one or more statement lines and one or more QBO transactions, then link them.
          </p>
        </div>
      </div>

      {collapsed ? null : (
      <>
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-4 text-sm">
            <div className="w-[150px] shrink-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Statement</p>
              <p className={METRIC_VALUE_ROW_CLASS}>
                {formatBankReconTableAmount(statementSum)}
              </p>
            </div>
            <div className="w-[150px] shrink-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">QBO</p>
              <p className={METRIC_VALUE_ROW_CLASS}>
                {formatBankReconTableAmount(qboSum)}
              </p>
            </div>
            <div className="w-[150px] shrink-0">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Difference</p>
              <div className={`${METRIC_VALUE_ROW_CLASS} gap-1`}>
                <span
                  className={
                    showDifferenceAlert
                      ? 'rounded bg-red-50 px-1.5 py-0.5 text-red-700'
                      : 'text-gray-900'
                  }
                >
                  {formatBankReconTableAmount(difference)}
                </span>
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center"
                  aria-hidden={!totalsBalanced}
                >
                  {totalsBalanced ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                  ) : null}
                </span>
              </div>
            </div>
          </div>
          {canEdit && selectionReady ? (
            <div className="flex items-center gap-2">
              <kbd className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-800">
                L
              </kbd>
              <Button
                type="button"
                variant="primary"
                gradient
                disabled={!linkEnabled}
                onClick={() => void handleLink()}
              >
                Link
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="border-b border-gray-200 bg-gray-50 px-5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">All unreconciled</span>
              <AllUnreconciledVisibilityToggle
                value={allUnreconciledVisibility}
                onChange={setAllUnreconciledVisibility}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Matched rows</span>
              <LinkedRowsVisibilityToggle
                value={linkedRowsVisibility}
                onChange={setLinkedRowsVisibility}
              />
            </div>
          </div>
          <SearchInput
            value={manualMatchSearch}
            onChange={setManualMatchSearch}
            placeholder="Search statement & QBO…"
            className="max-w-md"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 bg-gray-50 p-4 lg:grid-cols-2">
        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Statement only</h3>
          </div>
          <div className="max-h-[35rem] overflow-y-auto">
            <table className="table-forecast w-full">
              <thead className="sticky top-0 z-[1]">
                <tr>
                  {STATEMENT_COLUMNS.map((columnKey) => (
                    <th
                      key={columnKey}
                      className={`relative ${columnKey === 'amount' ? 'text-right' : ''}`}
                      style={fixedColumnStyle(getStatementWidth(columnKey))}
                    >
                      {STATEMENT_LABELS[columnKey]}
                      <ColumnResizeHandle columnKey={columnKey} onStartResize={startStatementResize} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleStatementLines.length === 0 ? (
                  <tr>
                    <td colSpan={STATEMENT_COLUMNS.length} className="py-8 text-center text-sm text-gray-500">
                      No statement-only rows.
                    </td>
                  </tr>
                ) : (
                  visibleStatementLines.map((line, index) => {
                    const selected = selectedStatementKeys.has(line.line_key)
                    const rowClass = line.linked
                      ? LINKED_ROW_CLASS
                      : selected
                        ? SELECTED_ROW_CLASS
                        : canEdit
                          ? UNSELECTED_ROW_CLASS
                          : ''
                    return (
                      <tr
                        key={line.line_key}
                        className={rowClass}
                        onMouseDown={(event) => {
                          if (event.shiftKey) event.preventDefault()
                        }}
                        onClick={(event) => handleStatementRowClick(line, index, event)}
                      >
                        {STATEMENT_COLUMNS.map((columnKey) =>
                          renderStatementCell(
                            columnKey,
                            line,
                            fixedColumnStyle(getStatementWidth(columnKey)),
                          ),
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">QBO only</h3>
          </div>
          <div className="max-h-[35rem] overflow-y-auto">
            <table className="table-forecast w-full">
              <thead className="sticky top-0 z-[1]">
                <tr>
                  {QBO_COLUMNS.map((columnKey) => (
                    <th
                      key={columnKey}
                      className={`relative ${columnKey === 'amount' ? 'text-right' : ''}`}
                      style={fixedColumnStyle(getQboWidth(columnKey))}
                    >
                      {QBO_LABELS[columnKey]}
                      <ColumnResizeHandle columnKey={columnKey} onStartResize={startQboResize} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleQboTxns.length === 0 ? (
                  <tr>
                    <td colSpan={QBO_COLUMNS.length} className="py-8 text-center text-sm text-gray-500">
                      No QBO rows match the current filters.
                    </td>
                  </tr>
                ) : (
                  visibleQboTxns.map((txn, index) => {
                    const qboLineKey = txn.qbo_line_key || txn.txn_id
                    const selected = selectedQboTxnIds.has(qboLineKey)
                    const rowClass = txn.ignored
                      ? IGNORED_ROW_CLASS
                      : txn.linked
                        ? LINKED_ROW_CLASS
                        : selected
                          ? SELECTED_ROW_CLASS
                          : canEdit
                            ? UNSELECTED_ROW_CLASS
                            : ''
                    return (
                      <tr
                        key={qboLineKey}
                        className={rowClass}
                        onMouseDown={(event) => {
                          if (event.shiftKey) event.preventDefault()
                        }}
                        onClick={(event) => handleQboRowClick(txn, index, event)}
                        onContextMenu={(event) => {
                          if (!canEdit) return
                          event.preventDefault()
                          setQboContextMenu({
                            txnId: txn.txn_id,
                            ignored: txn.ignored,
                            linked: Boolean(txn.linked),
                            ignoreOverrideId: txn.ignoreOverrideId,
                            linkOverrideIds: txn.linkOverrideIds,
                            matchLineKey: txn.matchLineKey,
                            x: event.clientX,
                            y: event.clientY,
                          })
                        }}
                      >
                        {QBO_COLUMNS.map((columnKey) =>
                          renderQboCell(
                            columnKey,
                            txn,
                            fixedColumnStyle(getQboWidth(columnKey)),
                          ),
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {qboContextMenu && canEdit ? (
        <QboRowContextMenu
          ignored={qboContextMenu.ignored}
          linked={qboContextMenu.linked}
          busy={ignoring}
          position={{ x: qboContextMenu.x, y: qboContextMenu.y }}
          onClose={() => setQboContextMenu(null)}
          onIgnore={() => void handleIgnoreQbo(qboContextMenu.txnId)}
          onRemoveIgnore={() => {
            if (qboContextMenu.ignoreOverrideId == null) return
            void handleRemoveQboIgnore(qboContextMenu.ignoreOverrideId, qboContextMenu.txnId)
          }}
          onUnlink={() => {
            if (!qboContextMenu.linkOverrideIds?.length) return
            const group = linkedGroups.find((item) =>
              item.items.some((entry) => entry.txn?.txn_id === qboContextMenu.txnId),
            )
            void handleUnlinkGroup(
              qboContextMenu.linkOverrideIds,
              group?.lines.map((line) => line.lineKey) ?? [],
            )
          }}
        />
      ) : null}

      {linkedGroups.length > 0 ? (
        <div className="border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
            aria-expanded={linkedThisMonthExpanded}
            onClick={() => setLinkedThisMonthExpanded((prev) => !prev)}
          >
            Linked this month ({linkedGroups.length})
            {linkedThisMonthExpanded ? (
              <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
          </button>
          {linkedThisMonthExpanded ? (
            <div className="mt-3 space-y-4">
              <SearchInput
                value={linkedThisMonthSearch}
                onChange={setLinkedThisMonthSearch}
                placeholder="Filter linked groups…"
                className="max-w-md"
              />
              {filteredLinkedGroups.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {linkedThisMonthSearch.trim()
                    ? 'No linked groups match the current filter.'
                    : 'No linked groups.'}
                </p>
              ) : null}
              {filteredLinkedGroups.map((group) => {
                const overrideIds = group.items.map((item) => item.overrideId)
                const lineTotal = sumSignedAmounts(uniqueLineAmountsForLinkedGroup(group))
                const txnTotal = sumSignedAmounts(uniqueTxnAmountsForLinkedGroup(group))
                const groupDiff = computeLinkerDifference(
                  uniqueLineAmountsForLinkedGroup(group),
                  uniqueTxnAmountsForLinkedGroup(group),
                )
                const showGroupDiffAlert = groupDiff !== 0
                const lineKeys = group.lines.map((line) => line.lineKey)
                return (
                  <div key={linkedMatchGroupReactKey(group)} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="text-sm text-gray-900">
                        <ul className="space-y-1">
                          {group.lines.map((lineEntry) => (
                            <li key={lineEntry.lineKey} className="font-medium">
                              {formatCalendarDate(lineEntry.line?.posted_date)} —{' '}
                              {lineEntry.line?.description ?? lineEntry.lineKey}
                              <span className="ml-2 tabular-nums font-normal text-gray-600">
                                {formatBankReconTableAmount(lineEntry.line?.amount_signed)}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 tabular-nums text-gray-600">
                          Statement {formatBankReconTableAmount(lineTotal)} · QBO{' '}
                          {formatBankReconTableAmount(txnTotal)} · Diff{' '}
                          <span
                            className={
                              showGroupDiffAlert
                                ? 'rounded bg-red-50 px-1 py-0.5 text-red-700'
                                : undefined
                            }
                          >
                            {formatBankReconTableAmount(groupDiff)}
                          </span>
                        </p>
                      </div>
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void handleUnlinkGroup(overrideIds, lineKeys)}
                        >
                          Unlink
                        </Button>
                      ) : null}
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-gray-700">
                      {group.items.map((item) => {
                        const txn = item.txn
                        const typeLabel = txn?.txn_type || '—'
                        const docLabel = txn?.doc_number || null
                        return (
                          <li key={item.overrideId} className="flex flex-wrap items-center gap-1.5">
                            {txn ? (
                              <>
                                <span className="tabular-nums">{formatCalendarDate(txn.txn_date)}</span>
                                {txn.url && typeLabel !== '—' ? (
                                  <a
                                    href={txn.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {typeLabel}
                                  </a>
                                ) : (
                                  <span>{typeLabel}</span>
                                )}
                                {docLabel ? <span>{docLabel}</span> : null}
                                <span className="tabular-nums">
                                  {formatBankReconTableAmount(txn.amount_signed)}
                                </span>
                              </>
                            ) : (
                              <span>Txn {item.overrideId}</span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      </>
      )}
    </section>
  )
}
